import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, CONTACT } from "../data/content";

const Logo = () => (
    <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
        <img
            src="/assets/logo-alzira.png"
            alt="Transportes Alzira — Desde 1998"
            className="h-11 w-11 rounded-full"
        />
        <span className="leading-none">
            <span className="block whitespace-nowrap font-display text-base font-black uppercase tracking-wide text-white">
                Transportes Alzira
            </span>
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
                Desde 1998
            </span>
        </span>
    </Link>
);

export const Header = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    return (
        <>
            <header
                className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl"
                data-testid="site-header"
            >
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-6 lg:px-10">
                    <Logo />
                    <div className="flex items-center gap-4 xl:gap-8">
                    <nav
                        className="hidden items-center gap-4 lg:flex xl:gap-7"
                        data-testid="desktop-nav"
                    >
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                                className={({ isActive }) =>
                                    `font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 hover:text-white xl:text-xs xl:tracking-[0.2em] ${
                                        isActive ? "text-brand" : "text-zinc-400"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                        <Link
                            to="/contactos"
                            data-testid="header-quote-button"
                            className="group hidden items-center gap-2 bg-brand px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink lg:inline-flex"
                        >
                            Pedir Orçamento
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="mobile-menu-button"
                            className="flex h-11 w-11 items-center justify-center border border-white/15 text-white lg:hidden"
                            aria-label="Abrir menu"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[60] flex flex-col bg-ink"
                        data-testid="mobile-menu"
                    >
                        <div className="flex h-20 items-center justify-between border-b border-white/10 px-6">
                            <Logo />
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="mobile-menu-close"
                                className="flex h-11 w-11 items-center justify-center border border-white/15 text-white"
                                aria-label="Fechar menu"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 * i, duration: 0.5 }}
                                >
                                    <NavLink
                                        to={l.to}
                                        onClick={() => setOpen(false)}
                                        data-testid={`mobile-nav-link-${l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                                        className={({ isActive }) =>
                                            `block border-b border-white/5 py-4 font-display text-4xl font-black uppercase tracking-tight transition-colors duration-300 ${
                                                isActive || (l.to === "/" && location.pathname === "/")
                                                    ? "text-brand"
                                                    : "text-white hover:text-brand"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="border-t border-white/10 px-8 py-6">
                            <Link
                                to="/contactos"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-quote-button"
                                className="flex w-full items-center justify-center gap-2 bg-brand py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white"
                            >
                                Pedir Orçamento
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                            <p className="mt-5 text-center font-mono text-xs text-zinc-500">
                                {CONTACT.phone} — {CONTACT.email}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
